import React, { useState, useEffect } from 'react';
import { 
  FiMail, 
  FiLock, 
  FiUser, 
  FiPhone, 
  FiEye, 
  FiEyeOff,
  FiArrowLeft,
  FiPhoneCall,
  FiCheckCircle,
  FiHome,
  FiUserCheck,
  FiSmartphone
} from 'react-icons/fi';
import { 
  FaGoogle, 
  FaFacebookF, 
  FaApple,
  FaWhatsapp,
  FaRegCreditCard
} from 'react-icons/fa';
import { MdOutlineLocalTaxi, MdOutlineSupportAgent } from 'react-icons/md';
import { BiSupport } from 'react-icons/bi';
import Logo from "../assets/WhatsApp_Image_2026-02-24_at_10.33.50_AM-removebg-preview.png";

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState('login'); // 'login' or 'signup'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Theme color
  const themeColor = '#3ead9a';
  const themeColorLight = '#e6f4f0';
  const themeColorDark = '#2d8a7a';

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    setActiveTab(isLogin ? 'login' : 'signup');
  }, [isLogin]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!isLogin) {
      if (!formData.name.trim()) {
        newErrors.name = 'Name is required';
      }
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required';
      } else if (!/^\d{10}$/.test(formData.phone)) {
        newErrors.phone = 'Invalid phone number';
      }
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!isLogin) {
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      if (!formData.agreeTerms) {
        newErrors.agreeTerms = 'You must agree to the terms';
      }
    }

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length === 0) {
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
        console.log('Form submitted:', formData);
      }, 1500);
    } else {
      setErrors(newErrors);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setErrors({});
    setFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      agreeTerms: false
    });
  };

  // Mobile App-like UI Styles
  const mobileStyles = {
    container: {
      minHeight: '100vh',
      background: '#ffffff',
      display: 'flex',
      flexDirection: 'column' ,
    },
    header: {
      background: `url('https://t3.ftcdn.net/jpg/02/75/03/42/360_F_275034287_RwBdkQQIvoYjxvHPocTR5MBrgQXFaZqr.jpg') no-repeat center/cover`,
      padding: '40px 20px 30px',
      borderBottomLeftRadius: '30px',
      borderBottomRightRadius: '30px',
      position: 'relative' ,
      overflow: 'hidden' ,
    },
    headerContent: {
      position: 'relative' ,
      zIndex: 2,
    },
    backButton: {
      width: 40,
      height: 40,
      borderRadius: 20,
      background: 'rgba(255,255,255,0.2)',
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      marginBottom: 20,
    },
    welcomeText: {
      color: '#ffffff',
      fontSize: 26,
      fontWeight: 700,
      marginBottom: 8,
    },
    subtitle: {
      color: 'rgba(255,255,255,0.9)',
      fontSize: 14,
      marginBottom: 30,
    },
    tabContainer: {
      display: 'flex',
      gap: 20,
      marginTop: 20,
    },
    tab: {
      flex: 1,
      padding: '12px 0',
      background: 'transparent',
      border: 'none',
      borderBottom: '2px solid rgba(255,255,255,0.3)',
      background: 'linear-gradient(135deg, #28aa96 0%, #28aa96 100%)',
      color: 'rgba(255,255,255,0.7)',
      fontSize: 16,
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s',
      borderRadius: 19,
    },
    activeTab: {
      borderBottom: `10px solid #000000`,
      color: '#ffffff',
    },
    formContainer: {
      flex: 1,
      padding: '30px 20px',
      background: '#ffffff',
    },
    inputGroup: {
      marginBottom: 20,
    },
    label: {
      display: 'block',
      fontSize: 13,
      fontWeight: 600,
      color: '#1e2b3a',
      marginBottom: 8,
    },
    inputWrapper: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
    inputIcon: {
      position: 'absolute' ,
      left: 16,
      color: themeColor,
      fontSize: 18,
    },
    input: {
      width: '100%',
      padding: '16px 16px 16px 48px',
      border: `1px solid ${errors.email || errors.password ? '#ef4444' : '#e2e8f0'}`,
      borderRadius: 16,
      fontSize: 15,
      outline: 'none',
      transition: 'all 0.2s',
      background: '#f8fafc',
    },
    passwordToggle: {
      position: 'absolute',
      right: 16,
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#94a3b8',
    },
    errorText: {
      color: '#ef4444',
      fontSize: 12,
      marginTop: 4,
      marginLeft: 4,
    },
    checkboxWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginBottom: 20,
    },
    checkbox: {
      width: 20,
      height: 20,
      borderRadius: 6,
      border: `2px solid ${themeColor}`,
      cursor: 'pointer',
    },
    checkboxLabel: {
      fontSize: 13,
      color: '#64748b',
    },
    link: {
      color: themeColor,
      textDecoration: 'none',
      fontWeight: 600,
    },
    button: {
      width: '100%',
      padding: '18px',
      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColorDark} 100%)`,
      color: '#ffffff',
      border: 'none',
      borderRadius: 16,
      fontSize: 16,
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s',
      marginBottom: 20,
      boxShadow: `0 8px 20px ${themeColor}40`,
    },
    divider: {
      textAlign: 'center' ,
      position: 'relative' ,
      margin: '24px 0',
      color: '#94a3b8',
      fontSize: 13,
    },
    dividerLine: {
      position: 'absolute' ,
      top: '50%',
      left: 0,
      right: 0,
      height: 1,
      background: '#e2e8f0',
      zIndex: 0,
    },
    dividerText: {
      position: 'relative' ,
      background: '#ffffff',
      padding: '0 12px',
      display: 'inline-block',
    },
    socialButtons: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: 12,
      marginBottom: 20,
    },
    socialButton: {
      padding: '14px',
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: 16,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    whatsappButton: {
      padding: '14px',
      background: '#25D36610',
      border: '1px solid #25D36630',
      borderRadius: 16,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      cursor: 'pointer',
      marginBottom: 16,
      color: '#075e54',
      fontWeight: 500,
    },
    footerText: {
      textAlign: 'center' ,
      fontSize: 14,
      color: '#64748b',
      marginTop: 16,
    },
    bottomNav: {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      height: 70,
      background: '#ffffff',
      borderTop: '1px solid #eef2f6',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-around',
      padding: '0 16px',
      zIndex: 100,
    },
    navItem: {
      display: 'flex',
      flexDirection: 'column' ,
      alignItems: 'center',
      gap: 4,
      color: '#64748b',
      fontSize: 11,
      textDecoration: 'none',
    },
    activeNavItem: {
      color: themeColor,
    },
    floatingShape: {
      position: 'absolute' ,
      width: 200,
      height: 200,
      borderRadius: '50%',
      background: 'rgba(255,255,255,0.1)',
      bottom: -50,
      right: -50,
    },
    floatingShape2: {
      position: 'absolute',
      width: 150,
      height: 150,
      borderRadius: '50%',
      background: 'rgba(255,255,255,0.1)',
      top: -30,
      left: -30,
    },
  };

  // Desktop Styles
  const desktopStyles = {
    container: {
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColorDark} 100%)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    },
    card: {
      width: '1000px',
      minHeight: '600px',
      background: '#ffffff',
      borderRadius: '24px',
      boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      display: 'flex',
      overflow: 'hidden',
      position: 'relative',
    },
    leftPanel: {
      flex: 1,
      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColorDark} 100%)`,
      padding: '40px',
      display: 'flex',
      flexDirection: 'column' ,
      justifyContent: 'space-between',
      position: 'relative' ,
      overflow: 'hidden',
    },
    rightPanel: {
      flex: 1,
      padding: '40px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      marginBottom: 40,
    },
    logoText: {
      fontSize: 24,
      fontWeight: 700,
      color: '#ffffff',
    },
    title: {
      fontSize: 36,
      fontWeight: 700,
      color: '#ffffff',
      marginBottom: 16,
      lineHeight: 1.2,
    },
    description: {
      fontSize: 16,
      color: 'rgba(255,255,255,0.9)',
      marginBottom: 40,
      lineHeight: 1.6,
    },
    featureItem: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      marginBottom: 16,
    },
    featureIcon: {
      width: 24,
      height: 24,
      borderRadius: 12,
      background: 'rgba(255,255,255,0.2)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    featureText: {
      color: '#ffffff',
      fontSize: 15,
    },
    quote: {
      color: 'rgba(255,255,255,0.8)',
      fontSize: 14,
      fontStyle: 'italic',
      marginTop: 'auto',
    },
    desktopFormTitle: {
      fontSize: 28,
      fontWeight: 700,
      color: '#1e2b3a',
      marginBottom: 8,
    },
    desktopSubtitle: {
      fontSize: 14,
      color: '#64748b',
      marginBottom: 24,
    },
    formWrapper: {
      maxWidth: 360,
      margin: '0 auto',
      width: '100%',
    },
  };

  // Use mobile or desktop styles based on device
  const styles = isMobile ? mobileStyles : desktopStyles;

  return (
    <div style={styles.container}>
      {isMobile ? (
        // Mobile App-like UI
        <>
          {/* Header with Gradient */}
          <div style={mobileStyles.header}>
            <div style={mobileStyles.floatingShape} />
            <div style={mobileStyles.floatingShape2} />
            
            <div style={mobileStyles.headerContent}>
              <button style={mobileStyles.backButton}>
                <FiArrowLeft size={20} color="#ffffff" />
              </button>
              
              <h1 style={mobileStyles.welcomeText}>
                {isLogin ? 'Safe Track Cab Service Welcome Back!' : 'Create Account'}
              </h1>
              <p style={mobileStyles.subtitle}>
                {isLogin 
                  ? 'Sign in to continue your journey' 
                  : 'Join us for a safe and comfortable ride'}
              </p>

              {/* Tab Switcher */}
              <div style={mobileStyles.tabContainer}>
                <button
                  style={{
                    ...mobileStyles.tab,
                    ...(activeTab === 'login' ? mobileStyles.activeTab : {})
                  }}
                  onClick={() => setIsLogin(true)}
                >
                  Sign In
                </button>
                <button
                  style={{
                    ...mobileStyles.tab,
                    ...(activeTab === 'signup' ? mobileStyles.activeTab : {})
                  }}
                  onClick={() => setIsLogin(false)}
                >
                  Sign Up
                </button>
              </div>
            </div>
          </div>

          {/* Form Section */}
          <div style={mobileStyles.formContainer}>
            <form onSubmit={handleSubmit}>
              {!isLogin && (
                <>
                  <div style={mobileStyles.inputGroup}>
                    <label style={mobileStyles.label}>Full Name</label>
                    <div style={mobileStyles.inputWrapper}>
                      <FiUser style={mobileStyles.inputIcon} />
                      <input
                        type="text"
                        name="name"
                        placeholder="Enter your full name"
                        value={formData.name}
                        onChange={handleChange}
                        style={mobileStyles.input}
                      />
                    </div>
                    {errors.name && <div style={mobileStyles.errorText}>{errors.name}</div>}
                  </div>

                  <div style={mobileStyles.inputGroup}>
                    <label style={mobileStyles.label}>Phone Number</label>
                    <div style={mobileStyles.inputWrapper}>
                      <FiSmartphone style={mobileStyles.inputIcon} />
                      <input
                        type="tel"
                        name="phone"
                        placeholder="9876543210"
                        value={formData.phone}
                        onChange={handleChange}
                        style={mobileStyles.input}
                      />
                    </div>
                    {errors.phone && <div style={mobileStyles.errorText}>{errors.phone}</div>}
                  </div>
                </>
              )}

              <div style={mobileStyles.inputGroup}>
                <label style={mobileStyles.label}>Email Address</label>
                <div style={mobileStyles.inputWrapper}>
                  <FiMail style={mobileStyles.inputIcon} />
                  <input
                    type="email"
                    name="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    style={mobileStyles.input}
                  />
                </div>
                {errors.email && <div style={mobileStyles.errorText}>{errors.email}</div>}
              </div>

              <div style={mobileStyles.inputGroup}>
                <label style={mobileStyles.label}>Password</label>
                <div style={mobileStyles.inputWrapper}>
                  <FiLock style={mobileStyles.inputIcon} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    style={mobileStyles.input}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={mobileStyles.passwordToggle}
                  >
                    {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </button>
                </div>
                {errors.password && <div style={mobileStyles.errorText}>{errors.password}</div>}
              </div>

              {!isLogin && (
                <div style={mobileStyles.inputGroup}>
                  <label style={mobileStyles.label}>Confirm Password</label>
                  <div style={mobileStyles.inputWrapper}>
                    <FiLock style={mobileStyles.inputIcon} />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      style={mobileStyles.input}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      style={mobileStyles.passwordToggle}
                    >
                      {showConfirmPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                  {errors.confirmPassword && <div style={mobileStyles.errorText}>{errors.confirmPassword}</div>}
                </div>
              )}

              {isLogin && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
                  <a href="#" style={mobileStyles.link}>Forgot Password?</a>
                </div>
              )}

              {!isLogin && (
                <div style={mobileStyles.checkboxWrapper}>
                  <input
                    type="checkbox"
                    name="agreeTerms"
                    id="terms"
                    checked={formData.agreeTerms}
                    onChange={handleChange}
                    style={mobileStyles.checkbox}
                  />
                  <label htmlFor="terms" style={mobileStyles.checkboxLabel}>
                    I agree to the <a href="#" style={mobileStyles.link}>Terms</a> & <a href="#" style={mobileStyles.link}>Privacy</a>
                  </label>
                </div>
              )}
              {errors.agreeTerms && <div style={mobileStyles.errorText}>{errors.agreeTerms}</div>}

              <button
                type="submit"
                style={{
                  ...mobileStyles.button,
                  opacity: isLoading ? 0.7 : 1,
                }}
                disabled={isLoading}
              >
                {isLoading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
              </button>

              <div style={mobileStyles.divider}>
                <span style={mobileStyles.dividerLine} />
                <span style={mobileStyles.dividerText}>or continue with</span>
              </div>

              <div style={mobileStyles.socialButtons}>
                <button style={mobileStyles.socialButton}>
                  <FaGoogle size={20} color="#DB4437" />
                </button>
                <button style={mobileStyles.socialButton}>
                  <FaFacebookF size={20} color="#4267B2" />
                </button>
                <button style={mobileStyles.socialButton}>
                  <FaApple size={20} color="#000000" />
                </button>
              </div>

              <div style={mobileStyles.whatsappButton}>
                <FaWhatsapp size={20} color="#25D366" />
                <span>Need help? WhatsApp us</span>
              </div>
            </form>
          </div>

          {/* Bottom Navigation */}
         
        </>
      ) : (
        // Desktop Professional UI
        <div style={desktopStyles.card}>
          {/* Left Panel - Branding */}
          <div style={desktopStyles.leftPanel}>
            <div style={{ position: 'relative', zIndex: 2 }}>
              <div style={desktopStyles.logo}>
                <img src={Logo} alt="Logo" style={{ width: 50, height: 50, objectFit: 'contain' }} />
                <span style={desktopStyles.logoText}>Safe Track</span>
              </div>
              
              <h2 style={desktopStyles.title}>
                {isLogin ? 'Welcome Back!' : 'Join Us Today!'}
              </h2>
              
              <p style={desktopStyles.description}>
                {isLogin 
                  ? 'Sign in to book your ride, track your journey, and enjoy exclusive benefits.'
                  : 'Create an account and experience the most reliable cab service in Chennai.'}
              </p>

              {/* Features */}
              <div>
                {[
                  '24/7 Cab Booking',
                  'Real-time Tracking',
                  'Multiple Payment Options',
                  'Verified Drivers'
                ].map((feature, idx) => (
                  <div key={idx} style={desktopStyles.featureItem}>
                    <div style={desktopStyles.featureIcon}>
                      <FiCheckCircle size={14} color="#ffffff" />
                    </div>
                    <span style={desktopStyles.featureText}>{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Floating Shapes */}
            <div style={mobileStyles.floatingShape} />
            <div style={mobileStyles.floatingShape2} />

            {/* Quote */}
            <div style={desktopStyles.quote}>
              "Safe, Reliable, Trusted - Your journey matters to us."
            </div>
          </div>

          {/* Right Panel - Form */}
          <div style={desktopStyles.rightPanel}>
            <div style={desktopStyles.formWrapper}>
              <h2 style={desktopStyles.desktopFormTitle}>
                {isLogin ? 'Sign In' : 'Create Account'}
              </h2>
              <p style={desktopStyles.desktopSubtitle}>
                {isLogin 
                  ? 'Welcome back! Please enter your details' 
                  : 'Get started with your free account'}
              </p>

              <form onSubmit={handleSubmit}>
                {!isLogin && (
                  <>
                    <div style={mobileStyles.inputGroup}>
                      <label style={mobileStyles.label}>Full Name</label>
                      <div style={mobileStyles.inputWrapper}>
                        <FiUser style={mobileStyles.inputIcon} />
                        <input
                          type="text"
                          name="name"
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={handleChange}
                          style={mobileStyles.input}
                        />
                      </div>
                      {errors.name && <div style={mobileStyles.errorText}>{errors.name}</div>}
                    </div>

                    <div style={mobileStyles.inputGroup}>
                      <label style={mobileStyles.label}>Phone Number</label>
                      <div style={mobileStyles.inputWrapper}>
                        <FiPhone style={mobileStyles.inputIcon} />
                        <input
                          type="tel"
                          name="phone"
                          placeholder="9876543210"
                          value={formData.phone}
                          onChange={handleChange}
                          style={mobileStyles.input}
                        />
                      </div>
                      {errors.phone && <div style={mobileStyles.errorText}>{errors.phone}</div>}
                    </div>
                  </>
                )}

                <div style={mobileStyles.inputGroup}>
                  <label style={mobileStyles.label}>Email Address</label>
                  <div style={mobileStyles.inputWrapper}>
                    <FiMail style={mobileStyles.inputIcon} />
                    <input
                      type="email"
                      name="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      style={mobileStyles.input}
                    />
                  </div>
                  {errors.email && <div style={mobileStyles.errorText}>{errors.email}</div>}
                </div>

                <div style={mobileStyles.inputGroup}>
                  <label style={mobileStyles.label}>Password</label>
                  <div style={mobileStyles.inputWrapper}>
                    <FiLock style={mobileStyles.inputIcon} />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      style={mobileStyles.input}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={mobileStyles.passwordToggle}
                    >
                      {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                  {errors.password && <div style={mobileStyles.errorText}>{errors.password}</div>}
                </div>

                {!isLogin && (
                  <div style={mobileStyles.inputGroup}>
                    <label style={mobileStyles.label}>Confirm Password</label>
                    <div style={mobileStyles.inputWrapper}>
                      <FiLock style={mobileStyles.inputIcon} />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        placeholder="••••••••"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        style={mobileStyles.input}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={mobileStyles.passwordToggle}
                      >
                        {showConfirmPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                      </button>
                    </div>
                    {errors.confirmPassword && <div style={mobileStyles.errorText}>{errors.confirmPassword}</div>}
                  </div>
                )}

                {isLogin && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <div style={mobileStyles.checkboxWrapper}>
                      <input type="checkbox" id="remember" style={mobileStyles.checkbox} />
                      <label htmlFor="remember" style={mobileStyles.checkboxLabel}>Remember me</label>
                    </div>
                    <a href="#" style={mobileStyles.link}>Forgot password?</a>
                  </div>
                )}

                {!isLogin && (
                  <div style={mobileStyles.checkboxWrapper}>
                    <input
                      type="checkbox"
                      name="agreeTerms"
                      id="terms"
                      checked={formData.agreeTerms}
                      onChange={handleChange}
                      style={mobileStyles.checkbox}
                    />
                    <label htmlFor="terms" style={mobileStyles.checkboxLabel}>
                      I agree to the <a href="#" style={mobileStyles.link}>Terms</a> and <a href="#" style={mobileStyles.link}>Privacy Policy</a>
                    </label>
                  </div>
                )}
                {errors.agreeTerms && <div style={mobileStyles.errorText}>{errors.agreeTerms}</div>}

                <button
                  type="submit"
                  style={{
                    ...mobileStyles.button,
                    opacity: isLoading ? 0.7 : 1,
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? 'Processing...' : isLogin ? 'Sign In' : 'Create Account'}
                </button>

                <div style={mobileStyles.divider}>
                  <span style={mobileStyles.dividerLine} />
                  <span style={mobileStyles.dividerText}>or continue with</span>
                </div>

                <div style={mobileStyles.socialButtons}>
                  <button style={mobileStyles.socialButton}>
                    <FaGoogle size={18} color="#DB4437" />
                  </button>
                  <button style={mobileStyles.socialButton}>
                    <FaFacebookF size={18} color="#4267B2" />
                  </button>
                  <button style={mobileStyles.socialButton}>
                    <FaApple size={18} color="#000000" />
                  </button>
                </div>

                <div style={mobileStyles.footerText}>
                  {isLogin ? "Don't have an account? " : "Already have an account? "}
                  <button
                    onClick={toggleMode}
                    style={{
                      ...mobileStyles.link,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 14,
                    }}
                  >
                    {isLogin ? 'Sign Up' : 'Sign In'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Global Styles */}
      <style>{`
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        
        button {
          cursor: pointer;
          transition: all 0.2s;
        }
        
        button:hover {
          opacity: 0.9;
          transform: translateY(-1px);
        }
        
        input:focus {
          border-color: ${themeColor};
          background: #ffffff;
          box-shadow: 0 0 0 3px ${themeColor}20;
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .fade-in {
          animation: slideUp 0.3s ease;
        }
      `}</style>
    </div>
  );
};

export default AuthPage;